import { WorksheetDefinition, WorksheetItem } from "../Types/WorksheetDefinition.Types";

/**
 * Answer-map validation against a worksheet definition — shared by the
 * server's PolicyWorksheetsService (operator saves) and the bot's eval
 * fixture seeder (declarative story `policyAnswers`), so the two paths
 * can't drift.
 *
 * Returns the first error message, or undefined when the map is valid.
 */
export function validateWorksheetAnswers(definition: WorksheetDefinition, answers: Record<string, string>): string | undefined {
  const itemsByKey = new Map(definition.items.map((item) => [item.itemKey, item]));
  for (const [itemKey, value] of Object.entries(answers)) {
    const item = itemsByKey.get(itemKey);
    if (!item) {
      return `Unknown item "${itemKey}"`;
    }
    const error = validateAnswerValue(item, value);
    if (error) {
      return error;
    }
  }
  return undefined;
}

/** Free-text values are short single-line strings dropped into one sentence. */
export const TEXT_VALUE_MAX_LENGTH = 80;

/** number_value answers are plain decimal numbers (digits, one optional decimal part). */
export const NUMBER_VALUE_PATTERN = /^\d+(\.\d+)?$/;
export const NUMBER_VALUE_MAX_LENGTH = 15;

/** One item's answer-value check — also used by definition validation to vet defaults. */
export function validateAnswerValue(item: WorksheetItem, value: string): string | undefined {
  switch (item.kind) {
    case "additive_checkbox":
    case "boolean_checkbox":
      return ["0", "1"].includes(value) ? undefined : `Item "${item.itemKey}" expects "0" or "1"`;
    case "radio":
    case "dropdown":
    case "parameterized_option":
      return item.options?.some((option) => option.optionKey === value) ? undefined : `Item "${item.itemKey}" has no option "${value}"`;
    case "multi_checkbox": {
      // Empty is valid — it means "none selected" and contributes nothing.
      if (value === "") {
        return undefined;
      }
      const tokens = value.split(",");
      const seen = new Set<string>();
      for (const token of tokens) {
        if (seen.has(token)) {
          return `Item "${item.itemKey}" has a duplicate option "${token}"`;
        }
        seen.add(token);
        if (!item.options?.some((option) => option.optionKey === token)) {
          return `Item "${item.itemKey}" has no option "${token}"`;
        }
      }
      return undefined;
    }
    case "text_value":
      // Empty is valid — it means "not provided" and contributes nothing.
      if (/[\r\n]/.test(value)) {
        return `Item "${item.itemKey}" must be a single line`;
      }
      if (value.length > TEXT_VALUE_MAX_LENGTH) {
        return `Item "${item.itemKey}" must be at most ${TEXT_VALUE_MAX_LENGTH} characters`;
      }
      // Guard the read-time reference resolver from operator-entered braces.
      if (value.includes("{{") || value.includes("}}")) {
        return `Item "${item.itemKey}" must not contain "{{" or "}}"`;
      }
      return undefined;
    case "number_value":
      // Empty is valid — it means "not provided" and contributes nothing.
      if (value === "") {
        return undefined;
      }
      if (value.length > NUMBER_VALUE_MAX_LENGTH) {
        return `Item "${item.itemKey}" must be at most ${NUMBER_VALUE_MAX_LENGTH} characters`;
      }
      return NUMBER_VALUE_PATTERN.test(value) ? undefined : `Item "${item.itemKey}" must be a number (digits with an optional decimal part)`;
    case "dynamic_reference":
    case "static_text":
    case "hint":
      return `Item "${item.itemKey}" does not take an answer`;
  }
}
